import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { useCart } from './CartContext';
import { shopApi } from '../services/api';
import { C, fmt } from './ShopLayout';

function Badge({ text, bg, color }) {
  return <span style={{ background: bg, color, fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4 }}>{text}</span>;
}

function ProductCard({ p, onAdd }) {
  const isNew = (Date.now() - new Date(p.created_at)) < 30 * 86400000;
  return (
    <div style={{ background: 'white', borderRadius: 12, border: `0.5px solid ${C.border}`, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {/* Badges */}
      <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', flexDirection: 'column', gap: 4, zIndex: 1 }}>
        {isNew && <Badge text="Novinka" bg="#EAF3DE" color="#3B6D11" />}
        {p.source_url && <Badge text="Z FB" bg="#E6F1FB" color="#185FA5" />}
        {p.stock_quantity <= 0 && !p.is_presale && <Badge text="Vyprodáno" bg="#f5f5f5" color="#999" />}
      </div>

      <Link to={`/shop/${p.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <div style={{ height: 200, background: '#f7f7f7', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {p.image_url
            ? <img src={p.image_url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 56 }}>📦</span>}
        </div>
        <div style={{ padding: '12px 14px 8px' }}>
          {p.category && <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{p.category}</div>}
          <div style={{ fontWeight: 500, fontSize: 14, lineHeight: 1.35, marginBottom: 8, color: C.text }}>{p.name}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.primary }}>{fmt(p.price_display || p.price_sell)}</div>
          {p.vat_registered && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>vč. DPH</div>}
        </div>
      </Link>

      <div style={{ padding: '0 14px 14px', marginTop: 'auto' }}>
        <button
          onClick={() => onAdd(p)}
          disabled={p.stock_quantity <= 0 && !p.is_presale}
          style={{ width: '100%', padding: '9px 0', background: p.stock_quantity <= 0 && !p.is_presale ? '#f0f0f0' : C.primary, color: p.stock_quantity <= 0 && !p.is_presale ? '#aaa' : 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: p.stock_quantity <= 0 && !p.is_presale ? 'default' : 'pointer' }}
        >
          {p.stock_quantity <= 0 && !p.is_presale ? 'Vyprodáno' : '+ Do košíku'}
        </button>
      </div>
    </div>
  );
}

export default function ShopHome() {
  const { settings } = useOutletContext();
  const { add } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  const category    = searchParams.get('category') || '';
  const search      = searchParams.get('search') || '';
  const sort        = searchParams.get('sort') || 'newest';
  const page        = parseInt(searchParams.get('page') || '1');
  const minPrice    = searchParams.get('minPrice') || '';
  const maxPrice    = searchParams.get('maxPrice') || '';
  const inStock     = searchParams.get('inStock') || '';
  const source      = searchParams.get('source') || '';

  const minRef = useRef(); const maxRef = useRef();

  useEffect(() => {
    shopApi.categories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 18, sort };
    if (category) params.category = category;
    if (search) params.search = search;
    if (minPrice) params.minPrice = minPrice;
    if (maxPrice) params.maxPrice = maxPrice;
    if (inStock) params.inStock = inStock;
    if (source) params.source = source;
    shopApi.products(params)
      .then(r => { setProducts(r.data); setTotal(r.total); setPages(r.pages); })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, search, sort, page, minPrice, maxPrice, inStock, source]);

  function setParam(key, val) {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  }

  function addToCart(p) {
    add(p, 1);
    setToast(`Přidáno: ${p.name}`);
    setTimeout(() => setToast(''), 2500);
  }

  const from = (page - 1) * 18 + 1;
  const to   = Math.min(page * 18, total);

  return (
    <div>
      {/* Hero */}
      <div style={{ background: C.bg, padding: '36px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
          <div style={{ maxWidth: 520 }}>
            {settings.hero_eyebrow && <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{settings.hero_eyebrow}</div>}
            <h1 style={{ fontSize: 30, fontWeight: 500, color: C.text, margin: '0 0 10px', lineHeight: 1.25 }}>{settings.hero_heading || settings.title}</h1>
            {settings.hero_subtext && <p style={{ fontSize: 15, color: C.muted, margin: '0 0 20px', lineHeight: 1.6 }}>{settings.hero_subtext}</p>}
            {settings.facebook_url && (
              <a href={settings.facebook_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.primary, color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Sledovat skupinu na Facebooku
              </a>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 500, color: C.teal, lineHeight: 1 }}>{settings.title?.split('—')[0]?.trim() || settings.title}</div>
            {settings.tagline && <div style={{ fontSize: 13, color: C.primary, marginTop: 4 }}>{settings.tagline}</div>}
          </div>
        </div>
      </div>

      {/* Kategorie bar */}
      <div style={{ borderBottom: `1px solid ${C.border}`, overflowX: 'auto', scrollbarWidth: 'none' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 0, whiteSpace: 'nowrap' }}>
          {[{ category: '', count: null, label: 'Vše' }, ...categories.map(c => ({ ...c, label: c.category }))].map(c => (
            <button key={c.category} onClick={() => setParam('category', c.category)}
              style={{ padding: '14px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: category === c.category ? C.primary : C.muted, borderBottom: `2px solid ${category === c.category ? C.primary : 'transparent'}`, transition: 'color 0.15s' }}>
              {c.label}{c.count != null ? ` (${c.count})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Hlavní obsah */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px', display: 'flex', gap: 32 }}>
        {/* Sidebar */}
        <aside style={{ width: 170, flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>Filtry</div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Cena</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input ref={minRef} defaultValue={minPrice} placeholder="od" type="number"
                onBlur={e => setParam('minPrice', e.target.value)}
                style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, outline: 'none' }} />
              <span style={{ color: C.muted, flexShrink: 0 }}>–</span>
              <input ref={maxRef} defaultValue={maxPrice} placeholder="do" type="number"
                onBlur={e => setParam('maxPrice', e.target.value)}
                style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, outline: 'none' }} />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Dostupnost</div>
            {[['true','Skladem'],['false','Na objednávku']].map(([val, label]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={inStock === val} onChange={e => setParam('inStock', e.target.checked ? val : '')} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Zdroj</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={source === 'fb'} onChange={e => setParam('source', e.target.checked ? 'fb' : '')} />
              Z FB příspěvku
            </label>
          </div>

          {(category || search || minPrice || maxPrice || inStock || source) && (
            <button onClick={() => setSearchParams({})} style={{ fontSize: 12, color: C.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              × Zrušit filtry
            </button>
          )}
        </aside>

        {/* Grid */}
        <div style={{ flex: 1 }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: C.muted }}>
              {loading ? 'Načítám…' : total === 0 ? 'Žádné produkty' : `Zobrazeno ${from}–${to} z ${total} produktů`}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <select value={sort} onChange={e => setParam('sort', e.target.value)}
                style={{ padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="newest">Nejnovější</option>
                <option value="price_asc">Cena ↑</option>
                <option value="price_desc">Cena ↓</option>
                <option value="bestseller">Nejprodávanější</option>
              </select>
              <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
                {['grid','list'].map(m => (
                  <button key={m} onClick={() => setViewMode(m)}
                    style={{ padding: '6px 10px', border: 'none', background: viewMode === m ? '#f0f0f0' : 'white', cursor: 'pointer', fontSize: 16 }}>
                    {m === 'grid' ? '⊞' : '☰'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Načítám…</div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Žádné produkty neodpovídají filtru</div>
          ) : viewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16 }}>
              {products.map(p => <ProductCard key={p.id} p={p} onAdd={addToCart} />)}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {products.map(p => (
                <div key={p.id} style={{ background: 'white', borderRadius: 12, border: `0.5px solid ${C.border}`, display: 'flex', gap: 16, padding: 16, alignItems: 'center' }}>
                  <div style={{ width: 80, height: 80, background: '#f7f7f7', borderRadius: 8, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {p.image_url ? <img src={p.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 36 }}>📦</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{p.category}</div>
                    <Link to={`/shop/${p.slug}`} style={{ fontWeight: 500, fontSize: 15, color: C.text, textDecoration: 'none' }}>{p.name}</Link>
                    {p.description && <div style={{ fontSize: 13, color: C.muted, marginTop: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.description}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.primary, marginBottom: 8 }}>{fmt(p.price_display || p.price_sell)}</div>
                    <button onClick={() => addToCart(p)} style={{ padding: '7px 16px', background: C.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>+ Přidat</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stránkování */}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 32 }}>
              <button onClick={() => setParam('page', page - 1)} disabled={page === 1}
                style={{ padding: '7px 14px', border: `1px solid ${C.border}`, borderRadius: 6, background: 'white', cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? '#ccc' : C.text }}>
                ‹
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1).filter(p => Math.abs(p - page) < 3 || p === 1 || p === pages).map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i-1] !== p - 1 && <span style={{ padding: '7px 4px', color: C.muted }}>…</span>}
                  <button onClick={() => setParam('page', p)}
                    style={{ padding: '7px 12px', border: `1px solid ${page === p ? C.primary : C.border}`, borderRadius: 6, background: page === p ? C.primary : 'white', color: page === p ? 'white' : C.text, cursor: 'pointer', fontWeight: page === p ? 600 : 400 }}>
                    {p}
                  </button>
                </React.Fragment>
              ))}
              <button onClick={() => setParam('page', page + 1)} disabled={page === pages}
                style={{ padding: '7px 14px', border: `1px solid ${C.border}`, borderRadius: 6, background: 'white', cursor: page === pages ? 'default' : 'pointer', color: page === pages ? '#ccc' : C.text }}>
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, background: C.teal, color: 'white', padding: '12px 20px', borderRadius: 10, fontWeight: 500, fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 300, transform: toast ? 'translateY(0)' : 'translateY(80px)', opacity: toast ? 1 : 0, transition: 'all 0.3s', pointerEvents: 'none' }}>
        ✓ {toast}
      </div>
    </div>
  );
}
